import { ToggleButton, Tooltip, makeStyles, tokens, shorthands, mergeClasses } from '@fluentui/react-components';
import { ReactNode, useEffect } from 'react';

import { TIcon } from '@shared/types';
import { ETiptapSelectorKey } from '../../../types/tiptap.types';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { Editor } from '@tiptap/core';
import { isLockedOutsideFields } from '../../../tiptap/extensions/TemplateLocking';
import useToolbarButtonStyles from '../../../hooks/useToolbarButtonStyles.hook';

const useStyles = makeStyles({
  default: {
    ...shorthands.borderColor('transparent'),
    fontWeight: 'normal',
  },
  active: {
    ...shorthands.borderColor(tokens.colorNeutralStrokeAccessible),
  },
  reducePadding: {
    minWidth: 0,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
});

type TProps = {
  selector: ETiptapSelectorKey | boolean | ((editor: Editor) => boolean);
  icon?: TIcon;
  children?: ReactNode;
  tooltip?: any;
  disabled?: boolean | ((editor: Editor) => boolean);
  /** Skip the locked-document gate: for actions that work without editing the doc (view aids). */
  worksWhenLocked?: boolean;
  className?: string;
  onClick: () => unknown;
};

const ToolbarToggleButton = ({
  selector,
  icon = undefined,
  children = null,
  tooltip = null,
  disabled = false,
  worksWhenLocked = false,
  onClick,
  className: defaultClassName = '',
}: TProps) => {
  const active = useEditorActive((editor) => ({
    isActive:
      typeof selector === 'function'
        ? selector(editor)
        : typeof selector === 'string'
          ? editor.isActive(selector)
          : undefined,
    isDisabled: typeof disabled === 'function' ? disabled(editor) : undefined,
    // Central gate: the locked body of a bound document silently filters every edit.
    lockDisabled: !worksWhenLocked && isLockedOutsideFields(editor.state),
  }));

  const isActive = typeof selector === 'boolean' ? selector : active.isActive;
  const isDisabled = (typeof disabled === 'boolean' ? disabled : active.isDisabled) || active.lockDisabled;

  const classes = useStyles();
  const toolbarClasses = useToolbarButtonStyles();

  let classNameButton = mergeClasses(isActive ? classes.active : classes.default, defaultClassName);
  if (children) classNameButton = mergeClasses(classNameButton, classes.reducePadding);
  if (isDisabled) classNameButton = mergeClasses(classNameButton, toolbarClasses.disabled);

  const content = (
    <ToggleButton icon={icon} checked={isActive} disabled={isDisabled} onClick={onClick} className={classNameButton}>
      {children}
    </ToggleButton>
  );
  if (!tooltip) return content;

  return (
    <Tooltip content={tooltip} relationship="label" withArrow>
      {content}
    </Tooltip>
  );
};

export default ToolbarToggleButton;
